<script setup lang="ts">
import {useToastrStore} from "../../store/toastr.js"
import {onBeforeUnmount, ref, watch} from "vue";

const store = useToastrStore()
const progress = ref(100)
let intervalId: ReturnType<typeof setInterval> | null = null

watch(() => store.message, () => {
    if (intervalId !== null) {
        clearInterval(intervalId)
        intervalId = null
    }

    if (store.message) {
        progress.value = 100
        const duration = 5000
        const intervalTime = 50
        const decrement = 100 / (duration / intervalTime)

        intervalId = setInterval(() => {
            progress.value -= decrement
            if (progress.value <= 0) {
                if (intervalId !== null) {
                    clearInterval(intervalId)
                    intervalId = null
                }
                store.hide()
            }
        }, intervalTime)
    } else {
        progress.value = 0
    }
}, {
    immediate: true
})

onBeforeUnmount(() => {
    if (intervalId !== null) {
        clearInterval(intervalId)
        intervalId = null
    }
})
</script>

<template>
<div class="toastr-container" aria-live="polite" aria-atomic="true">
    <transition name="toast-slide" appear>
        <div v-if="store.message" class="toastr">
            <div class="toastr__content">
                <p>{{ store.message }}</p>
            </div>
            <button class="toastr__close" type="button" aria-label="Dismiss toast" @click="store.hide()">
                <ah-icon name="cross-default" :size="12" />
            </button>
            <span class="toastr__progress" :style="{ transform: 'scaleX(' + progress / 100 + ')' }"></span>
        </div>
    </transition>
</div>
</template>

<style lang="scss">
.toastr-container {
    position: fixed;
    top: 1.5rem;
    right: 2rem;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    pointer-events: none;
}

.toastr {
    position: relative;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    column-gap: 1rem;
    padding: 1.2rem 1.6rem;
    min-width: 280px;
    max-width: 420px;
    border-radius: 1.2rem;
    background: var(--color-white);
    color: var(--color-grey-thunder);
    border: 1px solid var(--color-grey-dew);
    box-shadow: 0 1.6rem 4.2rem rgba(0, 22, 36, 0.12);
    pointer-events: auto;
    overflow: hidden;

    @include dark() {
        background: var(--color-darth-sidious);
        color: var(--color-white);
        border-color: var(--color-white-10);
        box-shadow: 0 1.6rem 4.2rem rgba(0, 0, 0, 0.45);
    }
}

.toastr__icon {
    width: 2.5rem;
    height: 2.5rem;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: var(--color-grey-dew);

    svg {
        width: 1.4rem;
        height: 1.4rem;
        fill: var(--color-primary);
    }

    @include dark() {
        background: var(--color-white-10);
    }
}

.toastr__content {
    p {
        margin: 0;
        @include font(1.4, 1.6, white);
    }
}

.toastr__close {
    border: none;
    background: transparent;
    font-size: 1.5rem;
    line-height: 1;
    color: var(--color-grey-hazy);
    cursor: pointer;
    transition: color 0.2s ease;

    .icon {
        width: 1.5rem;
        height: 1.5rem;
    }

    &:hover,
    &:focus-visible {
        color: var(--color-grey-thunder);
    }

    @include dark() {
        color: var(--color-white-60);

        &:hover,
        &:focus-visible {
            color: var(--color-white);
        }
    }
}

.toastr__progress {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 4px;
    background: var(--gradient-raspberry);
    transform-origin: left center;
    transition: transform 0.05s linear;

    @include dark() {
        background: var(--gradient-rosefinch);
    }
}

.toast-slide-enter-active,
.toast-slide-leave-active {
    transition: transform 0.35s ease, opacity 0.3s ease;
}

.toast-slide-enter-from,
.toast-slide-leave-to {
    transform: translate3d(20px, -20px, 0);
    opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
    .toast-slide-enter-active,
    .toast-slide-leave-active {
        transition: none;
    }

    .toast-slide-enter-from,
    .toast-slide-leave-to {
        transform: none;
    }
}
</style>
